import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { Building2, MapPin, Phone, Mail, CheckCircle, Download, Home, Sparkles, Instagram, Facebook } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [downloadType, setDownloadType] = useState("");
  const [isScheduleVisit, setIsScheduleVisit] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    visit_time: ""
  });
  const [downloadForm, setDownloadForm] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [loading, setLoading] = useState(false);

  const configurations = [
    {
      type: "1 BHK",
      area: "770 sq.ft",
      features: ["Spacious Living Room", "Modern Kitchen", "Luxury Bathroom", "Balcony"]
    },
    {
      type: "2 BHK",
      area: "1160 sq.ft",
      features: ["1 Master Bedroom", "1 Common Bedroom", "2 Premium Bathrooms", "Large Balcony"]
    },
    {
      type: "3 BHK",
      area: "1500 sq.ft",
      features: ["3 Spacious Bedrooms", "Premium Kitchen", "3 Luxury Bathrooms", "Multiple Balconies"]
    }
  ];

  const amenities = [
    { icon: "🌿", name: "83% Open Space", desc: "Vast open areas" },
    { icon: "🌳", name: "Garden 24000 SQ Ft", desc: "Lush landscaping" },
    { icon: "🏢", name: "Club House", desc: "Premium amenities" },
    { icon: "💪", name: "Fitness Centre", desc: "State-of-the-art gym" },
    { icon: "🚗", name: "Driver Room", desc: "Dedicated space" },
    { icon: "☀️", name: "Solar System", desc: "Eco-friendly power" },
    { icon: "🏛️", name: "Gazebo Sitting", desc: "Relaxation area" },
    { icon: "✨", name: "Decorative Lobby", desc: "Elegant entrance" },
    { icon: "💧", name: "Rain Water Harvesting", desc: "Sustainable living" },
    { icon: "🎭", name: "Multipurpose Hall", desc: "Events & gatherings" }
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Thank you! We'll contact you soon.");
      setShowContactDialog(false);
      setIsScheduleVisit(false);
      setContactForm({ name: "", email: "", phone: "", visit_time: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Save download request to database
      await axios.post(`${API}/download-request`, {
        ...downloadForm,
        document_type: downloadType
      });
      
      // Download the actual PDF file
      if (downloadType === 'brochure') {
        const link = document.createElement('a');
        link.href = 'https://customer-assets.emergentagent.com/job_malik-homes/artifacts/jkoiddj6_Kenhill%20Builders.pdf';
        link.download = 'Malik_Heights_Brochure.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Brochure download started!');
      } else if (downloadType === 'floorplan') {
        const link = document.createElement('a');
        link.href = 'https://customer-assets.emergentagent.com/job_malik-homes/artifacts/1mcf6ntp_Floor%20Plan.pdf';
        link.download = 'Malik_Heights_Floor_Plan.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Floor Plan download started!');
      } else {
        // For cost sheet, show a message (no file available yet)
        toast.success('Thank you! Our team will send you the cost sheet shortly.');
      }
      
      setShowDownloadDialog(false);
      setDownloadForm({ name: "", email: "", phone: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openDownloadDialog = (type) => {
    setDownloadType(type);
    setShowDownloadDialog(true);
  };

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <nav className="navbar">
          <div className="nav-content">
            <div className="logo" data-testid="logo">
              <div className="logo-container">
                <img src="https://customer-assets.emergentagent.com/job_malik-homes/artifacts/680hhmh9_LOGO.png" alt="Malik Heights" className="logo-image" />
                <p className="logo-tagline">We Built Trust</p>
              </div>
            </div>
            <div className="nav-links">
              <a href="#about" data-testid="nav-about">About</a>
              <a href="#configurations" data-testid="nav-configurations">Configurations</a>
              <a href="#amenities" data-testid="nav-amenities">Amenities</a>
              <a href="#grace-square" data-testid="nav-grace-square">Grace Square</a>
              <a href="#location" data-testid="nav-location">Location</a>
              <Button onClick={() => { setIsScheduleVisit(false); setShowContactDialog(true); }} className="contact-nav-btn" data-testid="nav-contact-btn">
                Contact Us
              </Button>
            </div>
          </div>
        </nav>
        
        <div className="hero-content">
          <div className="hero-badge" data-testid="hero-badge">
            <Sparkles className="badge-icon" />
            Luxury Living Redefined
          </div>
          <h1 className="hero-title" data-testid="hero-title">Welcome to<br />Malik Heights</h1>
          <p className="hero-developer" data-testid="hero-developer">
            A Project by KENHILL BUILDERS & DEVELOPERS
          </p>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Experience premium lifestyle with world-class amenities in the heart of the city
          </p>
          <div className="hero-actions">
            <Button onClick={() => { setIsScheduleVisit(true); setShowContactDialog(true); }} size="lg" className="hero-btn-primary" data-testid="hero-contact-btn">
              Schedule a Visit
            </Button>
            <Button onClick={() => openDownloadDialog('brochure')} size="lg" variant="outline" className="hero-btn-secondary" data-testid="hero-brochure-btn">
              <Download className="btn-icon" />
              Download Brochure
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section about-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" data-testid="about-title">About Malik Heights</h2>
            <p className="developer-badge" data-testid="developer-badge">
              By KENHILL BUILDERS & DEVELOPERS
            </p>
            <p className="section-subtitle" data-testid="about-subtitle">
              A masterpiece of modern architecture and thoughtful design
            </p>
          </div>
          <div className="about-content">
            <div className="about-image">
              <img src="https://customer-assets.emergentagent.com/job_malik-homes/artifacts/jvl9n1ny_b198f2ce-9054-4d6b-a624-a557d86ae322.jpg" alt="Malik Heights Tower" data-testid="about-image" />
            </div>
            <div className="about-text">
              <p data-testid="about-description">
                Malik Heights stands as a beacon of luxury and comfort, offering residents an unparalleled living experience. 
                Strategically located in a prime area, this residential tower combines contemporary design with sustainable 
                architecture to create homes that are both beautiful and functional.
              </p>
              <p data-testid="about-description-2">
                With meticulously planned 1, 2, and 3 BHK apartments, every unit is designed to maximize space, natural light, 
                and ventilation. Our commitment to quality and attention to detail ensures that Malik Heights is not just a 
                residence, but a lifestyle destination.
              </p>
              <div className="about-stats">
                <div className="stat-item" data-testid="stat-floors">
                  <div className="stat-number">23</div>
                  <div className="stat-label">Floors</div>
                </div>
                <div className="stat-item" data-testid="stat-units">
                  <div className="stat-number">345</div>
                  <div className="stat-label">Units</div>
                </div>
                <div className="stat-item" data-testid="stat-area">
                  <div className="stat-number">1.6</div>
                  <div className="stat-label">Acres</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Configurations Section */}
      <section id="configurations" className="section config-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" data-testid="config-title">Choose Your Perfect Home</h2>
            <p className="section-subtitle" data-testid="config-subtitle">
              Thoughtfully designed floor plans for every lifestyle
            </p>
          </div>
          <div className="config-grid">
            {configurations.map((config, index) => (
              <div key={index} className="config-card" data-testid={`config-card-${config.type.replace(' ', '-')}`}>
                <div className="config-header">
                  <Home className="config-icon" />
                  <h3 className="config-type" data-testid={`config-type-${index}`}>{config.type}</h3>
                </div>
                <div className="config-area" data-testid={`config-area-${index}`}>{config.area}</div>
                <div className="config-price" data-testid={`config-price-${index}`}>{config.price}</div>
                <div className="config-features">
                  {config.features.map((feature, idx) => (
                    <div key={idx} className="config-feature" data-testid={`config-feature-${index}-${idx}`}>
                      <CheckCircle className="feature-icon" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="config-actions">
                  <Button 
                    onClick={() => { setIsScheduleVisit(false); setShowContactDialog(true); }} 
                    className="config-btn"
                    data-testid={`config-enquire-btn-${index}`}
                  >
                    Enquire Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="download-section">
            <Button onClick={() => openDownloadDialog('costsheet')} size="lg" className="download-btn" data-testid="download-costsheet-btn">
              <Download className="btn-icon" />
              Download Cost Sheet
            </Button>
            <Button onClick={() => openDownloadDialog('floorplan')} size="lg" className="download-btn floor-plan-btn" data-testid="download-floorplan-btn">
              <Download className="btn-icon" />
              Download Floor Plan
            </Button>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section id="amenities" className="section amenities-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" data-testid="amenities-title">World-Class Amenities</h2>
            <p className="section-subtitle" data-testid="amenities-subtitle">
              Everything you need for a perfect lifestyle
            </p>
          </div>
          <div className="amenities-grid">
            {amenities.map((amenity, index) => (
              <div key={index} className="amenity-card" data-testid={`amenity-card-${index}`}>
                <div className="amenity-icon" data-testid={`amenity-icon-${index}`}>{amenity.icon}</div>
                <h4 className="amenity-name" data-testid={`amenity-name-${index}`}>{amenity.name}</h4>
                <p className="amenity-desc" data-testid={`amenity-desc-${index}`}>{amenity.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section gallery-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" data-testid="gallery-title">Gallery</h2>
            <p className="section-subtitle" data-testid="gallery-subtitle">
              A glimpse into your future home
            </p>
          </div>
          <div className="gallery-grid">
            <div className="gallery-item" data-testid="gallery-item-0">
              <img src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Apartment with Multiple Balconies" />
            </div>
            <div className="gallery-item" data-testid="gallery-item-1">
              <img src="https://images.unsplash.com/photo-1556911220-bff31c812dba?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Modular Kitchen" />
            </div>
            <div className="gallery-item" data-testid="gallery-item-2">
              <img src="https://images.unsplash.com/photo-1631679706909-1844bbd07221?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Living Room" />
            </div>
            <div className="gallery-item" data-testid="gallery-item-3">
              <img src="https://images.unsplash.com/photo-1616594039964-ae9021a400a0?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Bedroom" />
            </div>
            <div className="gallery-item" data-testid="gallery-item-4">
              <img src="https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Bathroom" />
            </div>
            <div className="gallery-item" data-testid="gallery-item-5">
              <img src="https://images.pexels.com/photos/164595/pexels-photo-164595.jpeg" alt="Amenities" />
            </div>
          </div>
        </div>
      </section>

      {/* Grace Square Section */}
      <section id="grace-square" className="section grace-square-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" data-testid="grace-square-title">Grace Square D1 & D2</h2>
            <p className="developer-badge" data-testid="grace-square-developer">By SKYLINE INFRA</p>
            <p className="section-subtitle" data-testid="grace-square-subtitle">
              22 Storey, 2 Towers - Kausa, Mumbra (Ongoing Project)
            </p>
          </div>

          <div className="grace-square-content">
            <div className="grace-square-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_malik-homes/artifacts/ece039il_image.png" 
                alt="Grace Square D1 & D2 Project" 
                className="grace-square-image"
                data-testid="grace-square-image"
              />
            </div>

            <div className="grace-square-details">
              <div className="grace-square-info-card">
                <div className="info-item">
                  <Building2 className="info-icon" />
                  <div>
                    <h4>Project Type</h4>
                    <p>Residential Towers</p>
                  </div>
                </div>
                <div className="info-item">
                  <Home className="info-icon" />
                  <div>
                    <h4>Towers</h4>
                    <p>2 Towers (D1 & D2)</p>
                  </div>
                </div>
                <div className="info-item">
                  <Building2 className="info-icon" />
                  <div>
                    <h4>Floors</h4>
                    <p>22 Storeys Each</p>
                  </div>
                </div>
                <div className="info-item">
                  <MapPin className="info-icon" />
                  <div>
                    <h4>Location</h4>
                    <p>Kausa, Mumbra</p>
                  </div>
                </div>
              </div>

              <div className="grace-square-status">
                <div className="status-badge ongoing">
                  <Sparkles className="status-icon" />
                  <span>Ongoing Project</span>
                </div>
                <p className="status-description">
                  Grace Square is our premium residential project featuring two magnificent 22-storey towers 
                  in the heart of Kausa, Mumbra. Experience modern living with world-class amenities and 
                  strategic location advantages.
                </p>
              </div>

              <div className="grace-square-documents">
                <div className="grace-doc-buttons">
                  <a 
                    href="https://customer-assets.emergentagent.com/job_e7cb9ddd-1ff4-439e-95ef-36bad319cbc5/artifacts/jezjx75h_Annexure%20-XI_%20MPCB-ENVIRONMENT_STATEMENT-0000090231.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="grace-doc-btn"
                    data-testid="grace-doc-mpcb"
                  >
                    Maharashtra Pollution Control Board File
                  </a>
                  <a 
                    href="https://customer-assets.emergentagent.com/job_e7cb9ddd-1ff4-439e-95ef-36bad319cbc5/artifacts/xz90tl6t_Annexure%20-I%20%20_%20EC%20copies%20and%20Transfer%20of%20%20EC.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="grace-doc-btn"
                    data-testid="grace-doc-ec"
                  >
                    Environment Clearance File
                  </a>
                  <a 
                    href="https://customer-assets.emergentagent.com/job_e7cb9ddd-1ff4-439e-95ef-36bad319cbc5/artifacts/vg7gq5ka_Six%20Compliance%20Monitoring%20Report_April%202025%20to%20September%202025_Shree%20Sumatinath%20Enterprises%20LLP_Grace%20Square.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="grace-doc-btn"
                    data-testid="grace-doc-compliance"
                  >
                    6 Monthly Compliance Report File
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="section location-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" data-testid="location-title">Prime Location</h2>
            <p className="section-subtitle" data-testid="location-subtitle">
              Strategically positioned for your convenience
            </p>
          </div>
          <div className="qr-code-section" data-testid="location-qr-code">
            <h3 className="qr-code-title">Scan to View Location</h3>
            <div className="qr-code-container">
              <img src="https://customer-assets.emergentagent.com/job_malik-homes/artifacts/9xlal3mm_image.png" alt="Scan QR Code for Location" className="qr-code-image" />
            </div>
            <p className="qr-code-description">Scan this QR code to get directions to Malik Heights</p>
          </div>

          <div className="location-content">
            <div className="location-highlights">
              <div className="location-item" data-testid="location-item-0">
                <MapPin className="location-icon" />
                <div>
                  <h4>Navi Mumbai International Airport</h4>
                  <p>25 minutes away</p>
                </div>
              </div>
              <div className="location-item" data-testid="location-item-1">
                <MapPin className="location-icon" />
                <div>
                  <h4>GHC Hospital</h4>
                  <p>5 minutes away</p>
                </div>
              </div>
              <div className="location-item" data-testid="location-item-2">
                <MapPin className="location-icon" />
                <div>
                  <h4>Upcoming Metro Train</h4>
                  <p>5 minutes away</p>
                </div>
              </div>
              <div className="location-item" data-testid="location-item-3">
                <MapPin className="location-icon" />
                <div>
                  <h4>Upcoming Bullet Train</h4>
                  <p>5 minutes away</p>
                </div>
              </div>
              <div className="location-item" data-testid="location-item-4">
                <MapPin className="location-icon" />
                <div>
                  <h4>Dosti School & Knowledge Hub</h4>
                  <p>5-6 minutes away</p>
                </div>
              </div>
              <div className="location-item" data-testid="location-item-5">
                <MapPin className="location-icon" />
                <div>
                  <h4>Airoli Katai Naka Road</h4>
                  <p>10 minutes away</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo" data-testid="footer-logo">
                <div className="logo-container">
                  <img src="https://customer-assets.emergentagent.com/job_malik-homes/artifacts/680hhmh9_LOGO.png" alt="Malik Heights" className="logo-image" />
                  <p className="logo-tagline">We Built Trust</p>
                </div>
              </div>
              <p className="footer-developer" data-testid="footer-developer">Project by Kenhill Builders and Developers</p>
              <div className="social-links">
                <a href="https://www.instagram.com/malikheights/" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="instagram-link">
                  <Instagram size={20} />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61583651455862&ref=ac_edit_ig_profile_ac" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="facebook-link">
                  <Facebook size={20} />
                </a>
              </div>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <div className="footer-contact">
                <div className="contact-item" data-testid="footer-phone">
                  <Phone size={16} />
                  <span>9833300426 / 9833300526</span>
                </div>
                <div className="contact-item" data-testid="footer-email">
                  <Mail size={16} />
                  <span>Info@MalikHeights.com</span>
                </div>
              </div>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <div className="footer-links">
                <a href="#about" data-testid="footer-link-about">About</a>
                <a href="#configurations" data-testid="footer-link-config">Configurations</a>
                <a href="#amenities" data-testid="footer-link-amenities">Amenities</a>
                <a href="#location" data-testid="footer-link-location">Location</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom" data-testid="footer-bottom">
            <p>© 2025 Malik Heights. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="dialog-content" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle data-testid="contact-dialog-title">
              {isScheduleVisit ? 'Schedule a Visit' : 'Get in Touch'}
            </DialogTitle>
            <DialogDescription data-testid="contact-dialog-desc">
              Fill in your details and we'll get back to you shortly
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="contact-form">
            <Input
              placeholder="Your Name"
              value={contactForm.name}
              onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
              required
              data-testid="contact-name-input"
            />
            <Input
              type="tel"
              placeholder="Phone Number"
              value={contactForm.phone}
              onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
              required
              data-testid="contact-phone-input"
            />
            <Input
              type="email"
              placeholder="Email Address"
              value={contactForm.email}
              onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
              required
              data-testid="contact-email-input"
            />
            {isScheduleVisit && (
              <Input
                type="datetime-local"
                placeholder="Preferred Visit Time"
                value={contactForm.visit_time}
                onChange={(e) => setContactForm({...contactForm, visit_time: e.target.value})}
                required
                data-testid="contact-visit-time-input"
              />
            )}
            <Button type="submit" disabled={loading} className="submit-btn" data-testid="contact-submit-btn">
              {loading ? "Submitting..." : isScheduleVisit ? "Schedule Visit" : "Enquire Now"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Download Dialog */}
      <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
        <DialogContent className="dialog-content" data-testid="download-dialog">
          <DialogHeader>
            <DialogTitle data-testid="download-dialog-title">
              Download {downloadType === 'brochure' ? 'Brochure' : downloadType === 'floorplan' ? 'Floor Plan' : 'Cost Sheet'}
            </DialogTitle>
            <DialogDescription data-testid="download-dialog-desc">
              Please provide your details to download
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDownloadSubmit} className="contact-form">
            <Input
              placeholder="Your Name"
              value={downloadForm.name}
              onChange={(e) => setDownloadForm({...downloadForm, name: e.target.value})}
              required
              data-testid="download-name-input"
            />
            <Input
              type="email"
              placeholder="Email Address"
              value={downloadForm.email}
              onChange={(e) => setDownloadForm({...downloadForm, email: e.target.value})}
              required
              data-testid="download-email-input"
            />
            <Input
              type="tel"
              placeholder="Phone Number"
              value={downloadForm.phone}
              onChange={(e) => setDownloadForm({...downloadForm, phone: e.target.value})}
              required
              data-testid="download-phone-input"
            />
            <Button type="submit" disabled={loading} className="submit-btn" data-testid="download-submit-btn">
              {loading ? "Processing..." : "Download Now"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;